import { afterNextRender, inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';

import type { SkTourHooks, TourConfig } from '@mercadoni/stark-ui';
import { catchError, defer, from, Observable, of, switchMap } from 'rxjs';

import { ToolRegistryService } from '../core/services/firestore/tool-registry.service';
import { RouteMetadataService } from '../core/services/navigation/route-metadata.service';
import { LayoutStateService } from '../core/services/ui/layout-state.service';
import { toolPathSegment } from '../shared/utils/tool-path.util';

/** Layout captured by `prepare()` so `restore()` can put it back. */
interface PreparedLayout {
  sidebarCollapsed: boolean;
  url: string;
  /** Router URL of the tool `prepare()` opened, or null when it did not navigate. */
  openedToolUrl: string | null;
}

/**
 * Puts Fury's shell into a tour-friendly state and back: closes the notifications drawer
 * (it covers tour popovers), expands the sidebar (the client-selector step needs it), and
 * opens a tool when the current page is not one (the favorite-star step needs a real tool).
 *
 * Provided as `SK_TOUR_HOOKS` in app.config.ts, so `SkTourService` runs it around every tour,
 * including the one launched from a What's New release.
 */
@Injectable({ providedIn: 'root' })
export class FuryTourHooks implements SkTourHooks {
  private readonly layout = inject(LayoutStateService);
  private readonly router = inject(Router);
  private readonly routeMetadata = inject(RouteMetadataService);
  private readonly toolRegistry = inject(ToolRegistryService);
  private readonly injector = inject(Injector);

  private prepared: PreparedLayout | null = null;

  /** Emits `true` once the layout is ready (after the next render), `false` if opening a tool fails. */
  prepare(_config: TourConfig): Observable<boolean> {
    return defer(() => {
      const prepared: PreparedLayout = {
        sidebarCollapsed: this.layout.sidebarCollapsed(),
        url: this.router.url,
        openedToolUrl: null,
      };
      this.prepared = prepared;
      this.layout.notificationsOpen.set(false);
      this.layout.sidebarCollapsed.set(false);

      const toolUrl = this.isToolUrl(prepared.url) ? null : this.firstToolUrl();
      const navigated$ = toolUrl ? from(this.router.navigateByUrl(toolUrl)) : of(true);

      return navigated$.pipe(
        switchMap((navigated) => {
          if (!navigated) return of(false);
          if (toolUrl) prepared.openedToolUrl = this.router.url;
          return this.afterRender();
        }),
        catchError(() => of(false)),
      );
    });
  }

  /** Restores the sidebar and leaves the tool `prepare()` opened. Safe to call repeatedly. */
  restore(): void {
    const prepared = this.prepared;
    if (!prepared) return;
    this.prepared = null;

    this.layout.sidebarCollapsed.set(prepared.sidebarCollapsed);
    // Only navigate back if the user is still on the tool we opened.
    if (prepared.openedToolUrl && this.router.url === prepared.openedToolUrl) {
      void this.router.navigateByUrl(prepared.url);
    }
  }

  /** `category/tool` routes only — admin pages have a feature id but no favoritable tool. */
  private isToolUrl(url: string): boolean {
    const path = url.split(/[?#]/)[0].split('/').filter(Boolean);
    return (
      path.length === 2 &&
      path[0] !== 'admin' &&
      this.routeMetadata.isRouteAvailable(path.join('/'))
    );
  }

  private firstToolUrl(): string | null {
    for (const category of this.toolRegistry.categories()) {
      const tool = category.tools[0];
      if (tool) return `/${category.id}/${toolPathSegment(tool)}`;
    }
    return null;
  }

  /** Emits once the shell has re-rendered, so step 1 measures the expanded sidebar. */
  private afterRender(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const ref = afterNextRender(
        () => {
          subscriber.next(true);
          subscriber.complete();
        },
        { injector: this.injector },
      );
      return () => ref.destroy();
    });
  }
}
